import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaReact, FaDatabase, FaServer, FaPython, FaJava, FaGithub, FaGitAlt, FaMobile, FaAws, FaDocker, FaLinux, FaCloud
} from 'react-icons/fa';
import { 
  SiFlutter, SiNextdotjs, SiFlask, SiNodedotjs, SiCplusplus, SiSpring, SiReact, SiMysql, SiJavascript,
  SiTypescript, SiTailwindcss, SiVercel, SiMicrosoftazure, SiVsco, SiIntellijidea,
  SiSlack, SiNotion, SiFigma, SiFirebase, SiPostgresql
} from 'react-icons/si';

const SkillsSection = styled.section`
  padding: 110px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.25rem, 5vw, 3.25rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #9beeff 58%, #7affc8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.7rem;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #7affc8, rgba(103, 216, 255, 0));
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionHeader = styled.div`
  max-width: 720px;
  margin-bottom: 3.25rem;
`;

const SectionLead = styled(motion.p)`
  color: #aebdcb;
  font-size: 1.02rem;
  line-height: 1.8;
  margin-top: 1.75rem;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 1.25rem;
  margin-bottom: 3rem;
`;

const SkillCategory = styled(motion.div)`
  background: rgba(255, 255, 255, 0.052);
  border: 1px solid rgba(255, 255, 255, 0.095);
  border-radius: 8px;
  padding: 1.35rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.075);
    border-color: rgba(128, 230, 255, 0.28);
    transform: translateY(-5px);
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.08rem;
  color: #f6fbff;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
`;

const SkillsList = styled.ul`
  list-style: none;
`;

const SkillItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.72rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const SkillName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #d3dfeb;
  font-weight: 500;
  min-width: 0;
`;

const SkillIcon = styled.div`
  font-size: 1rem;
  color: #9beeff;
  display: flex;
`;

const SkillLevel = styled.div`
  display: flex;
  gap: 0.3rem;
`;

const SkillDot = styled.div<{ filled: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${props => props.filled ? '#7affc8' : 'rgba(255, 255, 255, 0.16)'};
`;

const IconWrapper = styled.div`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 1.1rem;
  color: #7affc8;
  background: rgba(122, 255, 200, 0.1);
  border: 1px solid rgba(122, 255, 200, 0.16);
`;

const Skills: React.FC = () => {
  const skillCategories = [
    {
      title: 'Frontend Development',
      icon: <FaReact />,
      skills: [
        { name: 'React', level: 4, icon: <SiReact /> },
        { name: 'TypeScript', level: 3, icon: <SiTypescript /> },
        { name: 'Next.js', level: 2, icon: <SiNextdotjs /> },
        { name: 'JavaScript', level: 4, icon: <SiJavascript /> },
        { name: 'Tailwind CSS', level: 3, icon: <SiTailwindcss /> }
      ]
    },
    {
      title: 'Backend Development',
      icon: <FaServer />,
      skills: [
        { name: 'Spring', level: 5, icon: <SiSpring /> },
        { name: 'Flask', level: 4, icon: <SiFlask /> },
        { name: 'Node.js', level: 2, icon: <SiNodedotjs /> },
        { name: 'Firebase', level: 2, icon: <SiFirebase /> }
      ]
    },
    {
      title: 'Programming Languages',
      icon: <FaPython />,
      skills: [
        { name: 'Python', level: 4, icon: <FaPython /> },
        { name: 'Java', level: 4, icon: <FaJava /> },
        { name: 'JavaScript', level: 4, icon: <SiJavascript /> },
        { name: 'C', level: 3, icon: <FaDatabase /> },
        { name: 'C++', level: 3, icon: <SiCplusplus /> }
      ]
    },
    {
      title: 'Database & Storage',
      icon: <FaDatabase />,
      skills: [
        { name: 'MySQL', level: 4, icon: <SiMysql /> },
        { name: 'PostgreSQL', level: 4, icon: <SiPostgresql /> },
        { name: 'MongoDB', level: 3, icon: <FaDatabase /> },
        { name: 'Firebase', level: 2, icon: <SiFirebase /> }
      ]
    },
    {
      title: 'Mobile Development',
      icon: <FaMobile />,
      skills: [
        { name: 'React Native', level: 3, icon: <SiReact /> },
        { name: 'Flutter', level: 3, icon: <SiFlutter /> }
      ]
    },
    {
      title: 'Development Tools',
      icon: <FaGitAlt />,
      skills: [
        { name: 'Git', level: 4, icon: <FaGitAlt /> },
        { name: 'GitHub', level: 4, icon: <FaGithub /> },
        { name: 'VS Code', level: 4, icon: <SiVsco /> },
        { name: 'IntelliJ IDEA', level: 3, icon: <SiIntellijidea /> },
        { name: 'Cursor', level: 4, icon: <SiVsco /> }
      ]
    },
    {
      title: 'DevOps & Cloud',
      icon: <FaCloud />,
      skills: [
        { name: 'AWS', level: 4, icon: <FaAws /> },
        { name: 'Azure', level: 2, icon: <SiMicrosoftazure /> },
        { name: 'Docker', level: 4, icon: <FaDocker /> },
        { name: 'Linux', level: 3, icon: <FaLinux /> },
        { name: 'Vercel', level: 3, icon: <SiVercel /> }
      ]
    },
    {
      title: 'Design & Collaboration',
      icon: <SiFigma />,
      skills: [
        { name: 'Figma', level: 2, icon: <SiFigma /> },
        { name: 'Final Cut Pro', level: 2, icon: <FaMobile /> },
        { name: 'Slack', level: 3, icon: <SiSlack /> },
        { name: 'Notion', level: 3, icon: <SiNotion /> }
      ]
    }
  ];

  const renderSkillLevel = (level: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <SkillDot key={i} filled={i < level} />
    ));
  };

  return (
    <SkillsSection id="skills">
      <SectionHeader>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Skills & Technologies
        </SectionTitle>
        <SectionLead
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          백엔드와 프론트엔드를 함께 다루면서, 프로젝트 성격에 따라 모바일·클라우드·협업 도구까지 연결합니다.
        </SectionLead>
      </SectionHeader>
      
      <SkillsGrid>
        {skillCategories.map((category, index) => (
          <SkillCategory
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <CategoryTitle>
              <IconWrapper>{category.icon}</IconWrapper>
              {category.title}
            </CategoryTitle>
            <SkillsList>
              {category.skills.map((skill, skillIndex) => (
                <SkillItem key={skillIndex}>
                  <SkillName>
                    <SkillIcon>{skill.icon}</SkillIcon>
                    {skill.name}
                  </SkillName>
                  <SkillLevel>
                    {renderSkillLevel(skill.level)}
                  </SkillLevel>
                </SkillItem>
              ))}
            </SkillsList>
          </SkillCategory>
        ))}
      </SkillsGrid>
    </SkillsSection>
  );
};

export default Skills; 
